import type { Reference } from '@/features/sheets/contract/schema';

export type ScopeAgentMode = 'run' | 'update';

export type ScopeStructure =
  | { kind: 'library' }
  | { kind: 'volve' }
  | { kind: 'template'; documentIds: string[] }
  | { kind: 'tender_bill' };

export type ScopeStructureKind = ScopeStructure['kind'];

export type StartScopeAgentRun = {
  mode: ScopeAgentMode;
  structure?: ScopeStructure | null;
  knowledgeBaseIds?: string[];
  instructions?: string | null;
};

type FileCoverage = {
  docId: string;
  filename: string;
  state: 'cited' | 'no_scope' | 'not_read' | 'open';
  reason: string | null;
};

export type ScopeRunEnding =
  | 'time_limit'
  | 'allowance'
  | 'answer_refused'
  | 'agent_failed'
  | 'no_standard'
  | 'expired'
  | 'crashed';

export type ScopeAgentOutcome = {
  message: string;
  answerStatus: 'completed' | 'partial';
  docIds: string[];
  uncovered: FileCoverage[];
  noScope: number;
  usage: Record<string, number>;
  reason: ScopeRunEnding | null;
};

export type ScopeRunPhase = 'planning' | 'reading' | 'measuring' | 'trades' | 'lead';

export type ScopeTaskActivity = {
  kind: string;
  label: string;
  startedAt: string;
  finishedAt: string | null;
  status: string | null;
};

export type ScopeTabActivity = {
  tab: string;
  posts: number;
  withQuantity: number;
  assumptions: number;
  inputNeeded: number;
};

export type ScopeRunActivity = {
  startedAt: string;
  lastActivityAt: string;
  costUsd: number;
  tasks: ScopeTaskActivity[];
  tabs: ScopeTabActivity[];
  phase: ScopeRunPhase;
};

export type ScopeAgentRunView = {
  id: string;
  mode: ScopeAgentMode;
  state: 'running' | 'done' | 'failed';
  progress: number;
  artifactId: string | null;
  structure: ScopeStructure | null;
  knowledgeBaseIds: string[];
  instructions: string | null;
  language: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  outcome: ScopeAgentOutcome | null;
  reason: ScopeRunEnding | null;
  activity?: ScopeRunActivity | null;
};

export type ScopeRowCells = {
  code: string | null;
  kind: 'post' | 'note' | 'intro' | null;
  title: string | null;
  scope: string | null;
  description: string | null;
  quantity: number | null;
  unit: string | null;
  location: string | null;
  package: string | null;
  discipline: string | null;
  status: 'included' | 'excluded' | 'by_others' | 'clarify' | null;
  sources: Reference[];
};

export type ScopeRow = ScopeRowCells & {
  id: string;
  tabId: string;
  tab: string;
  parentId: string | null;
  group: boolean;
};

export const SCOPE_CONTRACT_PLACEMENT = {
  code: 'line',
  kind: 'meta',
  title: 'line',
  scope: 'line',
  description: 'meta',
  quantity: 'line',
  unit: 'line',
  location: 'field',
  package: 'meta',
  discipline: 'meta',
  status: 'line',
  sources: 'line',
} as const satisfies Record<keyof ScopeRowCells, 'line' | 'field' | 'meta'>;

export type KnowledgeBaseChoice = { id: string; name: string };

export type ScopeAgentStatus = {
  available?: boolean;
  evaluationId: string;
  artifactId: string | null;
  run: ScopeAgentRunView | null;
  knowledgeBases: KnowledgeBaseChoice[];
  hasTenderBill: boolean;
};

export enum ScopeAgentRunStatus {
  DONE = 20,
  FAILED = 30,
}

export type ScopeRunForm = {
  mode: ScopeAgentMode;
  structure: ScopeStructureKind | null;
  documentIds: string[];
  knowledgeBaseIds: string[];
  instructions: string;
};
